
import { Component, Vue } from "vue-property-decorator"
import { Getter, Action, namespace } from "vuex-class"

import Guid from "@/scripts/guid"

import Page from "@/components/Page.vue"
import Skeleton from "@/components/SkeletonLoader.vue"

import DashEditbar from "@/components/Dashboard/DashboardEditBar.vue"

import Numerical from "@/components/Dashboard/Numerical.vue"
import RecordView from "@/components/Dashboard/RecordView.vue"
import TeamView from "@/components/Dashboard/TeamView.vue"
import Graph from "@/components/Dashboard/Graph.vue"
import Metrics from "@/components/Dashboard/Metrics.vue"
import InsightTopResult from "@/components/Dashboard/InsightTopResult.vue"
import GoTo from "@/components/Dashboard/GoTo.vue"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import VueGridLayout, { GridItemData } from "@sureview/vue-grid"
import vSelect from "vue-select"

import CasePopup from "@/components/CaseDetails/CasePopup.vue"

import { DashboardSummary, Dashboard as Dash, Cell, CellType, User, CaseSummary } from "@/types"

import api from "@/services/api"
import dashboardApi from "@/services/api.dashboard"
import cloneDeep from "lodash/cloneDeep"
import DashboardImage from "@/components/Dashboard/DashboardImage.vue"
import Label from "@/components/Dashboard/Label.vue"
import DashboardCalendar from "@/components/Dashboard/DashboardCalendar.vue"
import { DashboardState } from "@/types/dashboard"
import NoticeboardDisplay from "@/components/Dashboard/NoticeboardDisplay.vue"
import DashboardGlobalParameters from "@/components/Dashboard/DashboardGlobalParameters.vue"
import { DynamicParameter, DynamicParameterValue } from "@/types/insights"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"

interface DashboardGridItem extends GridItemData {
	id?: string | null,
	pluginTypeId?: number,
    cellType: number;
    newCell?: boolean;
	contents?: any;
	isNew?: boolean;
}

const cellTypes: CellType[] = [
	{
		cellTypeId: 0,
		label: "Insight",
		component: "graph"
	},
	{
		cellTypeId: 1,
		label: "View",
		component: "record-view"
	},
	{
		cellTypeId: 2,
		label: "Team",
		component: "team-view"
	},
	{
		cellTypeId: 3,
		label: "Dashboard",
		component: "go-to"
	},
	{
		cellTypeId: 4,
		label: "Image",
		component: "dash-img"
	},
	{
		cellTypeId: 5,
		label: "Label",
		component: "dash-label"
	},
	{
		cellTypeId: 6,
		label: "Top Value",
		component: "insight-top-result"
	},
	{
		cellTypeId: 7,
		label: "Calendar",
		component: "dashboard-calendar"
	},
	{
		cellTypeId: 8,
		label: "Noticeboard",
		component: "noticeboard-display"
	}
]
const cellTypesById = cellTypes.toDictionary("cellTypeId")

enum Privacy {
	Public = 0,
	Teams = 1,
	Private = 2
}

const cases = namespace("cases")

@Component({
	components: {
		"page": PageWrapper,
		"skeleton": Skeleton,
		"scrollbar": VuePerfectScrollbar,
		"grid-layout": VueGridLayout.GridLayout,
		"grid-item": VueGridLayout.GridItem,
		"editbar": DashEditbar,
		"numerical": Numerical,
		"record-view": RecordView,
		"team-view": TeamView,
		"go-to": GoTo,
		"graph": Graph,
		"metrics": Metrics,
		"insight-top-result": InsightTopResult,
		"v-select": vSelect,
		"dash-img": DashboardImage,
		"dash-label": Label,
		"dashboard-calendar": DashboardCalendar,
		"case-popup": CasePopup,
		"noticeboard-display": NoticeboardDisplay,
		"global-parameters": DashboardGlobalParameters
	}
})
export default class Dashboard extends Vue {
	@Action loadTeams!: () => Promise<void>;

	@Getter("getMe")
	public me!: User;

	@cases.Action
	severitiesLoad!: () => Promise<void>

	private gridHeight: number = 720;
	private gridWidth: number = 1280;

	private standardColumns: number = 8;
	private fineColumns: number = 16;

	private dashboardState: DashboardState = {
		lastChangedFilterFromInsightId: "",
		calculationTableClickedRows: {}
	}

	public editing: boolean = false;
	public cellToAdd: any = null;

	private cellIdToFullscreen: any = null;

	public popupCaseSummary: CaseSummary | null = null
	public popupCloseCallback: ((caseSummary: CaseSummary) => void) | null = null

	public loading: boolean = true;
	public dashboardList: DashboardSummary[] = [];
	public currentDash: Dash | null = null;
	public uneditDash: Dash | null = null;
	public layout: DashboardGridItem[] = [];

	public deleteOpen: boolean = false;
	public deleteConfirmText: string = "";
	private readonly collator = new Intl.Collator("en");

	allGlobalParameters: DynamicParameter[] = []
	dashboardInsightParameters: DynamicParameter[] = []
	dynamicParameterValues: { [key: string]: DynamicParameterValue } = {}

	/**
	 * The dashboard list, sorted by title
	 * @private
	 */
	private get dashboardListSorted () {
		return this.dashboardList.sort((a, b) => this.collator.compare(a.title, b.title))
	}

	/**
	 * Gets the number of columns in the dashboard grid
	*/
	public get numberOfColumns () {
		if (!this.currentDash) return 0
		return this.currentDash.fineGridEnabled ? this.fineColumns : this.standardColumns
	}

	/** Gets the (rounded) height of each row in pixels */
	public get scaledRowHeight () {
		if (!this.currentDash) return 0
		return 320 // Height in pixels
	}

	/**
	 * Gets the (rounded) width of each column in pixels.
	 */
	public get scaledColumnWidth () {
		if (!this.currentDash) return 0
		return Math.round(this.gridWidth / this.numberOfColumns)
	}

	/**
	 * Gets the (un-rounded) width of each column in pixels.
	 */
	public get columnWidth () {
		if (!this.currentDash) return 0
		return this.gridWidth / this.numberOfColumns
	}

	public get cellTypes (): CellType[] {
		return cellTypes
	}

	private get rowCount () {
		if (!this.layout) {
			return 0
		}

		let maxX = this.numberOfColumns

		this.layout.forEach(cell => {
			const cellBottom = cell.y + cell.h
			if (cellBottom > maxX) {
				maxX = cellBottom
			}
		})

		if (this.editing) {
			maxX++
		}

		return maxX
	}

	public get editValid (): boolean {
		return !!this.currentDash && this.currentDash.title.length > 0 &&
			(this.currentDash.privacy !== Privacy.Teams || (this.currentDash.limitedToTeams && this.currentDash.limitedToTeams.length > 0))
	}

	public async mounted () {
		this.loadTeams()
		this.severitiesLoad()
		this.dashboardList = await dashboardApi.dashboardGetList()
		if (this.dashboardList.length > 0) {
			const lastDast = this.dashboardList.find(d => d.isLastViewedByUser)
			if (lastDast) {
				this.selectDashboard(lastDast.id!)
			} else {
				this.selectDashboard(this.dashboardList[0].id!)
			}
		}
		this.allGlobalParameters = await api.insightParameters()
		this.loading = false
	}

	public component (componentTypeId: number): string | null {
		const cellType = cellTypesById[componentTypeId]
		if (cellType) {
			return cellType.component
		}

		return null
	}

	private updateLayout (newLayout: any) {
		const layoutIds = new Set<string>()
		newLayout.forEach((layoutCell: any) => {
			layoutIds.add(layoutCell.i)
			if (layoutCell.pluginTypeId !== undefined) {
				layoutCell.id = layoutCell.i
				layoutCell.newCell = true
				layoutCell.cellTypeId = layoutCell.pluginTypeId
				layoutCell.contents = {}

				delete layoutCell.pluginTypeId
			} else {
				const cell = this.layout.find(c => c.id === layoutCell.id)
				if (cell) {
					cell.h = layoutCell.h
					cell.w = layoutCell.w
					cell.x = layoutCell.x
					cell.y = layoutCell.y
				}
			}
		})
	}

	private addCell (cellType: CellType) {
		if (!cellType || cellType.cellTypeId === undefined) {
			return
		}

		if (this.cellToAdd && this.cellToAdd.pluginTypeId === cellType.cellTypeId) {
			this.cellToAdd = null
			this.layout.removeWhere((layoutCell: any) => layoutCell.isNew)
			return
		}

		const id = Guid.newGuid()
		this.cellToAdd = {
			i: id,
			pluginTypeId: cellType.cellTypeId,
			viewCellId: id,
			newCell: true
		}
	}

	private setFineGrid (fineGridEnabled: boolean) {
		this.currentDash!.fineGridEnabled = fineGridEnabled
	}

	private finishAdd () {
		this.cellToAdd = null
		this.layout.forEach(layoutCell => {
			if (layoutCell.pluginTypeId !== undefined) {
				layoutCell.id = layoutCell.i
				layoutCell.newCell = true
				layoutCell.cellType = layoutCell.pluginTypeId
				layoutCell.contents = {}

				delete layoutCell.pluginTypeId
			}
		})
	}

	private async selectDashboard (dashboardId: string) {
		this.setDashboard(await dashboardApi.dashboardGet(dashboardId))
	}

	private setDashboard (dash: Dash) {
		this.dashboardInsightParameters = []

		this.currentDash = dash
		this.layout = this.currentDash!.cells.map(cell => {
			let id = ""
			if (cell.id != null) {
				id = cell.id
			}

			return {
				x: cell.cellPosition.x,
				y: cell.cellPosition.y,
				w: cell.cellSize.width,
				h: cell.cellSize.height,
				moved: false,
				isNew: false,
				i: id,
				id: cell.id,
				cellType: cell.cellType,
				contents: JSON.parse(cell.contents)
			}
		})
		this.dashboardState = {
			calculationTableClickedRows: {},
			lastChangedFilterFromInsightId: ""
		}
	}

	private async createNew () {
		this.currentDash = {
			id: null,
			title: "",
			cells: [],
			privacy: Privacy.Public,
			limitedToTeams: [],
			fineGridEnabled: false
		}
		this.layout = []
		this.editing = true
	}

	private async saveDashboard () {
		const layoutIds = new Set<string>()
		this.layout.forEach(layoutCell => {
			if (layoutCell.newCell) {
				this.currentDash!.cells.push({
					id: null,
					cellPosition: {
						x: layoutCell.x,
						y: layoutCell.y
					},
					cellSize: {
						width: layoutCell.w,
						height: layoutCell.h
					},
					contents: JSON.stringify(layoutCell.contents),
					cellType: layoutCell.cellType
				})
			} else {
				if (layoutCell.id != null) {
					layoutIds.add(layoutCell.id)
				}
				const existingCell = this.currentDash!.cells.find(c => c.id === layoutCell.id)
				if (existingCell) {
					existingCell.cellPosition.x = layoutCell.x
					existingCell.cellPosition.y = layoutCell.y
					existingCell.cellSize.width = layoutCell.w
					existingCell.cellSize.height = layoutCell.h
					existingCell.contents = JSON.stringify(layoutCell.contents)
				}
			}
		})

		var removeCells: Cell[] = []
		this.currentDash!.cells.forEach(cell => {
			if (cell.id && !layoutIds.has(cell.id!)) {
				removeCells.push(cell)
			}
		})
		removeCells.forEach(cell => {
			const cellIndex = this.currentDash!.cells.indexOf(cell)
			if (cellIndex > -1) {
				this.currentDash!.cells.splice(cellIndex, 1)
			}
		})

		this.setDashboard(await dashboardApi.dashboardSave(this.currentDash!))

		if (!this.dashboardList.find(d => d.id === this.currentDash!.id)) {
			this.dashboardList.push(this.currentDash!)
		}

		this.uneditDash = null
		this.cellToAdd = null
		this.editing = false
	}

	private startEdit (): void {
		this.uneditDash = this.currentDash
		this.currentDash = cloneDeep(this.currentDash)
		this.editing = true
	}

	private cancelEdit (): void {
		if (this.uneditDash) {
			this.setDashboard(this.uneditDash!)
		} else {
			if (this.dashboardList.length > 0) {
				this.selectDashboard(this.dashboardList[0].id!)
			} else {
				this.currentDash = null
			}
		}

		this.uneditDash = null
		this.cellToAdd = null
		this.editing = false
	}

	private deleteCurrent () {
		if (this.currentDash && this.currentDash.id) {
			this.deleteOpen = true
		}
	}

	private async deleteConfirm () {
		if (this.currentDash && this.currentDash.title === this.deleteConfirmText) {
			await dashboardApi.dashboardDelete(this.currentDash!.id!)
			const listIndex = this.dashboardList.findIndex(d => d.id === this.currentDash!.id)
			if (listIndex > -1) {
				this.dashboardList.splice(listIndex, 1)
			}

			this.currentDash = null
			if (this.dashboardList.length > 0) {
				this.selectDashboard(this.dashboardList[0].id!)
			}

			this.deleteOpen = false
			this.uneditDash = null
			this.cellToAdd = null
			this.editing = false
		}
	}

	private deleteCell (cell: any) {
		const cellIndex = this.layout.findIndex(c => c.i === cell.i)
		if (cellIndex > -1) {
			this.layout.splice(cellIndex, 1)
		}
	}

	private dashboardTrigger (value: any, triggerMethod: any) {
		if (triggerMethod === "goTo") {
			this.selectDashboard(value.id)
		}
	}

	private popupOpen (caseSummary: CaseSummary, callback: ((caseSummary: CaseSummary) => void) | null) {
		this.popupCaseSummary = caseSummary
		this.popupCloseCallback = callback
	}

	private popupClosed (caseSummary: CaseSummary) {
		if (this.popupCloseCallback) {
			this.popupCloseCallback(caseSummary)
		}
	}

	private calculationTableRowClick (insightId: string, selectedGroupRow: any) {
		const clickedRow = this.dashboardState.calculationTableClickedRows[insightId]
		this.dashboardState.lastChangedFilterFromInsightId = insightId
		const clickedAlready = !!clickedRow && clickedRow.value === selectedGroupRow.value
		if (clickedAlready) {
			Vue.set(this.dashboardState.calculationTableClickedRows, insightId, undefined)
		} else {
			Vue.set(this.dashboardState.calculationTableClickedRows, insightId, selectedGroupRow)
		}
	}

	/**
	 * Adds the global insight parameters to the dashboard parameters
	 */
	private addParametersToDash (includedIds: string[]) {
		var includedParameters = this.allGlobalParameters.filter(param => includedIds.includes(param.key))

		includedParameters.forEach(param => {
			if (!this.dashboardInsightParameters.find(p => p.key === param.key)) {
				if (param.defaultValue && (!this.dynamicParameterValues[param.key] || !this.dynamicParameterValues[param.key].value)) {
					const value = {
						key: param.key,
						dataType: param.dataType,
						value: param.defaultValue,
						dynamicValue: param.dynamicValue || false,
						searchClauseParameters: param.searchClauseParameters
					}

					this.setParameter(param.key, value)
				}

				this.dashboardInsightParameters.push(param)
			}
		})
	}

	// Call setParameter for all charts on the dashboard
	private setParameter (key: string, value: DynamicParameterValue | null) {
		Vue.set(this.dynamicParameterValues, key, value)

		this.layout.filter(c => c.cellType === 0).forEach(c => {
			var cell:any = this.$refs["cell" + c.id]
			cell[0].setParameter(key, value)
		})
	}
}
